//TODO Migrar pra versão 4 assim que possível
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { z } from "zod";

import { DATE_PATTERNS } from "../../Mantine";
import { CONTACT_METHODS } from "../zod.constants";
import { MESSAGE } from "../zod.messages";
import { REGEX } from "../zod.regex";
import { hasRepeatedChars, isSequenciaNumerica, replaceNonNumericChars } from "../zod.utils";

dayjs.extend(customParseFormat);

const REQUIRED = z.string(MESSAGE.GLOBAL.REQUIRED).nonempty({ message: MESSAGE.GLOBAL.NOT_EMPTY });
const _OPTIONAL = z.string().optional();

const cpfSchema = REQUIRED.superRefine((val, ctx) => {
  const cpf = val.replace(/[.\\-]/g, "");

  if (cpf.length !== 11 || !/^\d+$/.test(cpf)) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.CPF.INVALID
    });
  }

  if (hasRepeatedChars(cpf, cpf.length)) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.GLOBAL.NOT_REPEATED
    });
  }

  if (isSequenciaNumerica(cpf, cpf.length)) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.GLOBAL.NOT_SEQUENCED
    });
  }

  // Validação dos dígitos verificadores
  const calcCheckDigit = (cpf: string, factor: number) => {
    let total = 0;
    for (let i = 0; i < factor - 1; i++) {
      total += parseInt(cpf.charAt(i)) * (factor - i);
    }
    const rest = (total * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  const digit1 = calcCheckDigit(cpf, 10);
  const digit2 = calcCheckDigit(cpf, 11);
  if (digit1 !== parseInt(cpf.charAt(9)) || digit2 !== parseInt(cpf.charAt(10))) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.CPF.INVALID
    });
  }
});

const birthday = REQUIRED.superRefine((val, ctx) => {
  const date = dayjs(val, DATE_PATTERNS.BR.DATE, true);

  if (!date.isValid()) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.BIRTHDAY.INVALID
    });
  }

  const now = dayjs();

  if (date.isAfter(now)) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.BIRTHDAY.NOT_IN_FUTURE
    });
  }

  if (now.diff(date, "year") < 18) {
    ctx.addIssue({
      code: "custom",
      message: MESSAGE.BIRTHDAY.NOT_YOUNGER_THAN_18
    });
  }
}).transform((val) => dayjs(val, DATE_PATTERNS.BR.DATE).format(DATE_PATTERNS.GLOBAL.DATE));

//! Common schema for primitives
const COMMON_SCHEMA = {
  PRIMITIVES: {
    STRING: {
      NOT_EMPTY: REQUIRED
    },

    NUMBER: {
      NOT_EMPTY: z.number(MESSAGE.GLOBAL.REQUIRED).min(1, { message: MESSAGE.GLOBAL.MIN_LENGTH(1) })
    }
  }
};

//! Global schema for various fields
const GLOBAL_SCHEMA = {
  NAME: REQUIRED.trim()
    .min(2, { message: MESSAGE.GLOBAL.MIN_LENGTH(2) })
    .max(80, { message: MESSAGE.GLOBAL.MAX_LENGTH(80) })
    .regex(REGEX.PRIMITIVES.STRING.ONLY_CHARS_WITH_SPACES, MESSAGE.PRIMITIVES.STRING.ONLY_CHARS),
  CNH: REQUIRED.length(11, { message: MESSAGE.GLOBAL.LENGTH(11) })
    .regex(REGEX.PRIMITIVES.NUMBER.ONLY_NUMBERS, MESSAGE.PRIMITIVES.NUMBER.ONLY_NUMBERS)
    .superRefine((val, ctx) => {
      if (isSequenciaNumerica(val, val.length)) {
        ctx.addIssue({
          code: "custom",
          message: MESSAGE.GLOBAL.NOT_SEQUENCED
        });
      }

      if (hasRepeatedChars(val, val.length)) {
        ctx.addIssue({
          code: "custom",
          message: MESSAGE.GLOBAL.NOT_REPEATED
        });
      }
    }),
  CPF: cpfSchema,
  PLATE: z.string(MESSAGE.GLOBAL.REQUIRED).trim().length(7, MESSAGE.GLOBAL.LENGTH_FIELD("Placa", 7)).regex(REGEX.PLATE, MESSAGE.PLATE.INVALID_FIELD("Placa")),
  RENAVAM: REQUIRED.trim().refine(
    (val) => {
      const renavam = val.replaceAll("-", "");
      const isValid = REGEX.RENAVAM.test(renavam) && renavam.length === 11;

      return isValid;
    },
    MESSAGE.GLOBAL.LENGTH_FIELD("Renavam", 11)
  ),
  BIRTHDAY: birthday,
  EMAIL: REQUIRED.trim().email({ message: MESSAGE.EMAIL.INVALID }),
  PHONE: REQUIRED.trim().superRefine((val, ctx) => {
    const phone = replaceNonNumericChars(val);

    if (phone.length < 10 || phone.length > 11) {
      ctx.addIssue({
        code: "custom",
        message: MESSAGE.PHONE.INVALID
      });
    }

    if (!/^\d+$/.test(phone)) {
      ctx.addIssue({
        code: "custom",
        message: MESSAGE.PRIMITIVES.NUMBER.ONLY_NUMBERS
      });
    }
  }),

  USER: z.object({
    name: REQUIRED.trim()
      .min(2, { message: MESSAGE.GLOBAL.MIN_LENGTH(2) })
      .max(80, { message: MESSAGE.GLOBAL.MAX_LENGTH(80) })
      .regex(REGEX.PRIMITIVES.STRING.ONLY_CHARS_WITH_SPACES, MESSAGE.PRIMITIVES.STRING.ONLY_CHARS),
    cpf: cpfSchema,
    cnh: REQUIRED.length(11, { message: MESSAGE.GLOBAL.LENGTH(11) })
      .regex(REGEX.PRIMITIVES.NUMBER.ONLY_NUMBERS, MESSAGE.PRIMITIVES.NUMBER.ONLY_NUMBERS)
      .superRefine((val, ctx) => {
        if (isSequenciaNumerica(val, val.length)) {
          ctx.addIssue({
            code: "custom",
            message: MESSAGE.GLOBAL.NOT_SEQUENCED
          });
        }

        if (hasRepeatedChars(val, val.length)) {
          ctx.addIssue({
            code: "custom",
            message: MESSAGE.GLOBAL.NOT_REPEATED
          });
        }
      }),
    birthDate: birthday,
    phone: REQUIRED.trim().superRefine((val, ctx) => {
      const phone = replaceNonNumericChars(val);

      if (phone.length < 10 || phone.length > 11) {
        ctx.addIssue({
          code: "custom",
          message: MESSAGE.PHONE.INVALID
        });
      }

      if (!/^\d+$/.test(phone)) {
        ctx.addIssue({
          code: "custom",
          message: MESSAGE.PRIMITIVES.NUMBER.ONLY_NUMBERS
        });
      }
    }),
    email: REQUIRED.trim().email({ message: MESSAGE.EMAIL.INVALID })
  }),

  TRUCK: z.object({
    plate: z.string(MESSAGE.GLOBAL.REQUIRED).trim().length(7, MESSAGE.GLOBAL.LENGTH_FIELD("Placa", 7)).regex(REGEX.PLATE, MESSAGE.PLATE.INVALID_FIELD("Placa")),
    renavam: REQUIRED.trim().refine(
      (val) => {
        const renavam = val.replaceAll("-", "");
        const isValid = REGEX.RENAVAM.test(renavam) && renavam.length === 11;

        return isValid;
      },
      MESSAGE.GLOBAL.LENGTH_FIELD("Renavam", 11)
    ),
    brand: COMMON_SCHEMA.PRIMITIVES.STRING.NOT_EMPTY,
    model: COMMON_SCHEMA.PRIMITIVES.STRING.NOT_EMPTY,
    color: COMMON_SCHEMA.PRIMITIVES.STRING.NOT_EMPTY,
    implementType: COMMON_SCHEMA.PRIMITIVES.STRING.NOT_EMPTY,
    shaftQuantity: z
      .number(MESSAGE.GLOBAL.REQUIRED)
      .min(1, MESSAGE.GLOBAL.MIN_LENGTH_FIELD("Quantidade de eixos", 1))
      .max(12, MESSAGE.GLOBAL.MAX_LENGTH_FIELD("Quantidade de eixos", 12)),
    grossCapacity: z
      .number(MESSAGE.GLOBAL.REQUIRED)
      .min(1, MESSAGE.GLOBAL.MIN_LENGTH_FIELD("Capacidade bruta", 1))
      .max(100, MESSAGE.GLOBAL.MAX_LENGTH_FIELD("Capacidade bruta", 100)),
    netCapacity: z
      .number(MESSAGE.GLOBAL.REQUIRED)
      .min(1, MESSAGE.GLOBAL.MIN_LENGTH_FIELD("Capacidade líquida", 1))
      .max(1000000, MESSAGE.GLOBAL.MAX_LENGTH_FIELD("Capacidade líquida", 1000000))
  }),

  REQUIRED: {
    CPF: cpfSchema,
    BIRTHDAY: birthday
  },

  /* OPTIONAL DATA */
  OPTIONAL: {
    CONTACT: z.array(z.enum(CONTACT_METHODS)),
    TERMS: z.object({
      id: z.number().optional(),
      updatedAt: z.string().datetime({ offset: true }).optional()
    })
  }
};

export * from "./Contact";
export { GLOBAL_SCHEMA };
